import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaHeart } from 'react-icons/fa';
import { moodService } from '../../../services/mood.service';
import { mindfulnessService } from '../../../services/mindfulness.service';
import { useAuth } from '../../../context/AuthContext';
import './Summary.css';

const WellnessSummary = () => {
  const [moodStats, setMoodStats] = useState({});
  const [mindfulnessStats, setMindfulnessStats] = useState({
    total: 0,
    thisWeek: 0,
    totalMinutes: 0
  });
  const { user } = useAuth();

  useEffect(() => {
    let unsubscribe;

    if (user) {
      unsubscribe = moodService.subscribeMoods(user.uid, (moods) => {
        const stats = moods.reduce((acc, mood) => {
          acc[mood.emoji] = (acc[mood.emoji] || 0) + 1;
          return acc;
        }, {});
        setMoodStats(stats);
      });
    }

    return () => unsubscribe && unsubscribe();
  }, [user]);

  useEffect(() => {
    let unsubscribe;

    if (user) {
      unsubscribe = mindfulnessService.subscribeSessions(user.uid, (sessions) => {
        const now = new Date();
        const weekAgo = new Date(now.setDate(now.getDate() - 7));

        const stats = sessions.reduce((acc, session) => {
          acc.total++;
          if (session.duration) {
            acc.totalMinutes += session.duration;
          }
          if (session.createdAt && session.createdAt.toDate() > weekAgo) {
            acc.thisWeek++;
          }
          return acc;
        }, { total: 0, thisWeek: 0, totalMinutes: 0 });

        setMindfulnessStats(stats);
      });
    }

    return () => unsubscribe && unsubscribe();
  }, [user]);

  const topMoods = Object.entries(moodStats)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const totalMoods = Object.values(moodStats).reduce((a, b) => a + b, 0);

  return (
    <div className="summary-card">
      <div className="summary-header">
        <h2>Wellness</h2>
        <FaHeart className="summary-icon" />
      </div>

      <div className="summary-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <span role="img" aria-label="meditation">🧘</span>
          </div>
          <div className="stat-info">
            <span className="stat-value">{mindfulnessStats.thisWeek}</span>
            <span className="stat-label">Sessions this week</span>
          </div>
        </div>

        <div className="stat-item">
          <div className="stat-icon">
            <span role="img" aria-label="timer">⏱️</span>
          </div>
          <div className="stat-info">
            <span className="stat-value">{mindfulnessStats.totalMinutes}</span>
            <span className="stat-label">Total Minutes</span>
          </div>
        </div>
      </div>

      {topMoods.length > 0 && (
        <div className="mood-tracker">
          <div className="mood-tracker-header">
            <span className="mood-tracker-title">Most Common Moods</span>
            <span className="mood-tracker-total">{totalMoods} total</span>
          </div>
          <div className="mood-pills">
            {topMoods.map(([emoji, count], index) => (
              <motion.div 
                key={emoji} 
                className="mood-pill"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <span className="mood-emoji">{emoji}</span>
                <span className="mood-count">{count}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessSummary; 